import { useState, useContext } from "react";
import { useFinancials } from "../../hooks/useFinancials";
import FinancialMetricCard from "./FinancialMetricCard";
import RevenueChart from "./RevenueChart";
import ExpenseChart from "./ExpenseChart";
import { exportToCSV, exportToPDF } from "../../services/financialReportService";
import { CurrentUserContext } from "../../contexts/CurrentUserContext";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str + "T12:00:00").toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function daysBetween(a, b) {
  return Math.round((new Date(b + "T12:00:00") - new Date(a + "T12:00:00")) / 86_400_000);
}

// ─── Shared styles ────────────────────────────────────────────────────────────

const SECTION = {
  background: "#fff",
  border: "1px solid #35454C",
  borderRadius: 12,
  padding: "1.25rem 1.4rem",
  marginBottom: "1.25rem",
  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
};

const SECTION_TITLE = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "1rem",
  fontWeight: 700,
  color: "#0C2D48",
  margin: "0 0 1rem",
};

const inp = {
  padding: "0.45rem 0.7rem", borderRadius: 7, border: "1px solid #ddd",
  fontSize: "0.88rem", fontFamily: "Raleway, sans-serif",
  background: "#fff", color: "#333",
};

const btnPrimary = {
  padding: "0.5rem 1.1rem", background: "#E4E69B", border: "none",
  borderRadius: 7, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#0C2D48",
};

const btnSmall = {
  padding: "0.22rem 0.65rem", background: "#f3f3f3", border: "1px solid #ddd",
  borderRadius: 6, fontSize: "0.76rem", cursor: "pointer",
  fontFamily: "Raleway, sans-serif", color: "#333",
};

// ─── Date Range Selector ──────────────────────────────────────────────────────

const PRESETS = [
  { value: "this-month", label: "This month" },
  { value: "last-month", label: "Last month" },
  { value: "this-quarter", label: "This quarter" },
  { value: "last-quarter", label: "Last quarter" },
  { value: "this-year", label: "This year (Apr–Mar)" },
  { value: "last-year", label: "Last year" },
  { value: "custom", label: "Custom range" },
];

function DateRangeSelector({ preset, setPreset, customStart, setCustomStart, customEnd, setCustomEnd, start, end }) {
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 20,
      background: "#fff", borderBottom: "1px solid #e8e8e8",
      padding: "0.75rem 1.4rem",
      display: "flex", alignItems: "center", flexWrap: "wrap",
      gap: "0.6rem",
    }}>
      <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "#aaa", fontFamily: "Raleway, sans-serif", whiteSpace: "nowrap" }}>
        PERIOD
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
        {PRESETS.map(p => (
          <button
            key={p.value}
            onClick={() => setPreset(p.value)}
            style={{
              padding: "0.3rem 0.75rem",
              borderRadius: 20,
              border: `1px solid ${preset === p.value ? "#0C2D48" : "#ddd"}`,
              background: preset === p.value ? "#0C2D48" : "#fff",
              color: preset === p.value ? "#fff" : "#555",
              fontSize: "0.78rem", fontWeight: preset === p.value ? 700 : 500,
              cursor: "pointer", fontFamily: "Raleway, sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={inp} />
          <span style={{ color: "#aaa", fontSize: "0.8rem" }}>to</span>
          <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={inp} />
        </div>
      )}
      {start && end && preset !== "custom" && (
        <span style={{ fontSize: "0.75rem", color: "#aaa", fontFamily: "Raleway, sans-serif", marginLeft: "0.25rem" }}>
          {fmtDate(start)} — {fmtDate(end)}
        </span>
      )}
    </div>
  );
}

// ─── Income Breakdown Horizontal Bars ────────────────────────────────────────

function IncomeBreakdown({ byProject, onOpenProjectFinancials }) {
  const maxInvoiced = Math.max(...byProject.map(p => p.invoiced), 1);

  return (
    <div>
      {/* Horizontal bars */}
      <div style={{ marginBottom: "1.25rem" }}>
        {byProject.map((p, i) => {
          const invPct = (p.invoiced / maxInvoiced) * 100;
          const rcvPct = (p.received / maxInvoiced) * 100;
          return (
            <div key={p.id ?? "none"} style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.55rem" }}>
              {/* Name */}
              <div style={{ width: 130, textAlign: "right", fontFamily: "Raleway, sans-serif", fontSize: "0.82rem", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>
                {onOpenProjectFinancials && p.id ? (
                  <span
                    onClick={() => onOpenProjectFinancials(p.id)}
                    style={{ cursor: "pointer", color: "#2a6080", fontWeight: 600, textDecoration: "underline", textDecorationColor: "#82B3CC", textUnderlineOffset: "2px" }}
                  >
                    {p.name}
                  </span>
                ) : (
                  <span style={{ color: p.id ? "#2a6080" : "#aaa", fontWeight: p.id ? 600 : 400 }}>{p.name}</span>
                )}
              </div>
              {/* Bar track */}
              <div style={{ flex: 1, position: "relative", height: 22, background: "#f0f4f8", borderRadius: 5, overflow: "hidden" }}>
                {/* Invoiced (light blue) */}
                <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${invPct}%`, background: "#82B3CC", borderRadius: 5 }} />
                {/* Received (navy, inset) */}
                <div style={{ position: "absolute", left: 0, top: 4, height: 14, width: `${rcvPct}%`, background: "#0C2D48", borderRadius: 5 }} />
              </div>
              {/* Amount */}
              <div style={{ width: 88, textAlign: "right", fontSize: "0.82rem", fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "Raleway, sans-serif" }}>
                {fmtMoney(p.invoiced)}
              </div>
            </div>
          );
        })}
        <div style={{ display: "flex", gap: "1rem", marginTop: "0.6rem", paddingLeft: 145 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.72rem", color: "#82B3CC", fontFamily: "Raleway, sans-serif" }}>
            <div style={{ width: 12, height: 6, background: "#82B3CC", borderRadius: 2 }} /> Invoiced
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.72rem", color: "#0C2D48", fontFamily: "Raleway, sans-serif" }}>
            <div style={{ width: 12, height: 6, background: "#0C2D48", borderRadius: 2 }} /> Received
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #f0f0f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 540, fontFamily: "Raleway, sans-serif" }}>
          <thead>
            <tr style={{ background: "#f8f9fb" }}>
              {["Project", "Invoiced", "Paid", "Outstanding", "Expenses", "Net"].map(h => (
                <th key={h} style={{
                  padding: "0.5rem 0.75rem", fontSize: "0.75rem", fontWeight: 700,
                  color: "#aaa", textAlign: h === "Project" ? "left" : "right",
                  borderBottom: "1px solid #e8e8e8", whiteSpace: "nowrap",
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {byProject.map(p => (
              <tr key={p.id ?? "none"} style={{ borderBottom: "1px solid #f5f5f5" }}
                onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <td style={{ padding: "0.55rem 0.75rem", fontSize: "0.85rem", fontWeight: 600, color: p.id ? "#2a6080" : "#aaa" }}>
                  {p.name}
                </td>
                <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.85rem", fontWeight: 700, color: "#0C2D48", whiteSpace: "nowrap" }}>
                  {fmtMoney(p.invoiced)}
                </td>
                <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.85rem", color: "#1e6645", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fmtMoney(p.received)}
                </td>
                <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.85rem", color: p.invoiced - p.received > 0 ? "#7a4a10" : "#aaa", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fmtMoney(p.invoiced - p.received)}
                </td>
                <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.85rem", color: "#A12710", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {fmtMoney(p.expenses)}
                </td>
                <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", fontSize: "0.85rem", fontWeight: 700, color: p.net >= 0 ? "#1e6645" : "#A12710", whiteSpace: "nowrap" }}>
                  {fmtMoney(p.net)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Outstanding Actions ──────────────────────────────────────────────────────

function OutstandingActions({ overdueInvoices, upcomingInvoices, unbilledExpenses, onNavigateInvoicing, allProjects }) {
  const today = new Date().toISOString().slice(0, 10);
  const projectName = pid => {
    if (!pid) return "—";
    const p = allProjects.find(p => String(p.id) === String(pid));
    return p ? p.name : "—";
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem" }}>

      {/* Column 1 — Overdue invoices */}
      <div style={{ ...SECTION, marginBottom: 0 }}>
        <h3 style={{ ...SECTION_TITLE, color: "#A12710" }}>
          Overdue invoices
          {overdueInvoices.length > 0 && (
            <span style={{ marginLeft: "0.5rem", background: "#fde8e8", color: "#A12710", border: "1px solid #f5a5a5", borderRadius: 10, padding: "0.1rem 0.45rem", fontSize: "0.72rem", fontWeight: 700 }}>
              {overdueInvoices.length}
            </span>
          )}
        </h3>
        {overdueInvoices.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }}>No overdue invoices.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {overdueInvoices.map(inv => {
              const daysOver = daysBetween(inv.dueDate, today);
              const email = inv.contactEmail || "";
              const subject = encodeURIComponent(`Following up on invoice ${inv.number}`);
              const mailto = `mailto:${email}?subject=${subject}&body=`;
              return (
                <div key={inv.id} style={{ padding: "0.6rem 0.75rem", background: "#fde8e8", borderRadius: 8, border: "1px solid #f5a5a5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0C2D48", fontFamily: "Raleway, sans-serif" }}>
                        {inv.contactName || inv.contactCompany || inv.number}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#A12710", fontFamily: "Raleway, sans-serif" }}>
                        {daysOver} day{daysOver !== 1 ? "s" : ""} overdue
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, fontSize: "0.95rem", color: "#A12710", whiteSpace: "nowrap", fontFamily: "Raleway, sans-serif" }}>
                      {fmtMoney(inv.lineItems?.reduce((s, r) => s + r.qty * r.unitPrice * (1 + (r.tax || 0) / 100), 0) || 0)}
                    </div>
                  </div>
                  {email && (
                    <a
                      href={mailto}
                      style={{
                        display: "inline-block", marginTop: "0.45rem",
                        padding: "0.2rem 0.6rem", background: "#0C2D48",
                        color: "#fff", borderRadius: 6, fontSize: "0.75rem",
                        fontWeight: 600, textDecoration: "none",
                        fontFamily: "Raleway, sans-serif",
                      }}
                    >
                      Chase →
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Column 2 — Upcoming due */}
      <div style={{ ...SECTION, marginBottom: 0 }}>
        <h3 style={{ ...SECTION_TITLE, color: "#7a4a10" }}>
          Due in 14 days
          {upcomingInvoices.length > 0 && (
            <span style={{ marginLeft: "0.5rem", background: "#fff8ee", color: "#7a4a10", border: "1px solid #FBC490", borderRadius: 10, padding: "0.1rem 0.45rem", fontSize: "0.72rem", fontWeight: 700 }}>
              {upcomingInvoices.length}
            </span>
          )}
        </h3>
        {upcomingInvoices.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }}>Nothing due soon.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {upcomingInvoices.map(inv => {
              const daysLeft = daysBetween(today, inv.dueDate);
              return (
                <div key={inv.id} style={{ padding: "0.6rem 0.75rem", background: "#fff8ee", borderRadius: 8, border: "1px solid #FBC490" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0C2D48", fontFamily: "Raleway, sans-serif" }}>
                        {inv.contactName || inv.contactCompany || inv.number}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#7a4a10", fontFamily: "Raleway, sans-serif" }}>
                        {daysLeft <= 0 ? "Due today" : `Due in ${daysLeft} day${daysLeft !== 1 ? "s" : ""}`}
                        {" · "}{fmtDate(inv.dueDate)}
                      </div>
                    </div>
                    <div style={{ fontWeight: 900, fontSize: "0.95rem", color: "#7a4a10", whiteSpace: "nowrap", fontFamily: "Raleway, sans-serif" }}>
                      {fmtMoney(inv.lineItems?.reduce((s, r) => s + r.qty * r.unitPrice * (1 + (r.tax || 0) / 100), 0) || 0)}
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigateInvoicing && onNavigateInvoicing("Invoices")}
                    style={{ ...btnSmall, marginTop: "0.45rem" }}
                  >
                    View
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Column 3 — Unbilled expenses */}
      <div style={{ ...SECTION, marginBottom: 0 }}>
        <h3 style={{ ...SECTION_TITLE }}>
          Unbilled expenses
          {unbilledExpenses.length > 0 && (
            <span style={{ marginLeft: "0.5rem", background: "#f0f4f8", color: "#0C2D48", border: "1px solid #35454C", borderRadius: 10, padding: "0.1rem 0.45rem", fontSize: "0.72rem", fontWeight: 700 }}>
              {unbilledExpenses.length}
            </span>
          )}
        </h3>
        {unbilledExpenses.length === 0 ? (
          <p style={{ color: "#aaa", fontSize: "0.85rem", margin: 0 }}>No unbilled expenses.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {unbilledExpenses.slice(0, 6).map(exp => (
              <div key={exp.id} style={{ padding: "0.6rem 0.75rem", background: "#f8f9fb", borderRadius: 8, border: "1px solid #e8e8e8" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0C2D48", fontFamily: "Raleway, sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {exp.title}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#888", fontFamily: "Raleway, sans-serif" }}>
                      {projectName(exp.project_id)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#7a4a10", whiteSpace: "nowrap", fontFamily: "Raleway, sans-serif" }}>
                    {fmtMoney(exp.amount)}
                  </div>
                </div>
                <button
                  onClick={() => onNavigateInvoicing && onNavigateInvoicing("Invoices")}
                  style={{ ...btnSmall, marginTop: "0.45rem", background: "#eaf6ef", color: "#1e6645", border: "1px solid #6abf85" }}
                >
                  Add to invoice
                </button>
              </div>
            ))}
            {unbilledExpenses.length > 6 && (
              <p style={{ fontSize: "0.78rem", color: "#aaa", margin: "0.25rem 0 0", fontFamily: "Raleway, sans-serif" }}>
                +{unbilledExpenses.length - 6} more · go to Invoicing &gt; Expenses
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Annual Summary Strip ─────────────────────────────────────────────────────

function AnnualSummaryStrip({ summary }) {
  if (!summary) return null;
  const { taxYearLabel, ytdInvoiced, ytdReceived, ytdExpenses, ytdNet, projectedInvoiced, lastYearInvoiced } = summary;

  const cells = [
    { label: "YTD invoiced", value: fmtMoney(ytdInvoiced), color: "#0C2D48" },
    { label: "YTD received", value: fmtMoney(ytdReceived), color: "#1e6645" },
    { label: "YTD expenses", value: fmtMoney(ytdExpenses), color: "#A12710" },
    { label: "YTD net", value: fmtMoney(ytdNet), color: ytdNet >= 0 ? "#1e6645" : "#A12710" },
    { label: "Projected full year", value: fmtMoney(projectedInvoiced), color: "#7a4a10" },
    { label: "Last year total", value: fmtMoney(lastYearInvoiced), color: "#888" },
  ];

  return (
    <div style={{ background: "#fff", border: "1px solid #35454C", borderRadius: 12, padding: "1.1rem 1.4rem", marginBottom: "1.25rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.85rem", flexWrap: "wrap", gap: "0.5rem" }}>
        <h3 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontSize: "0.88rem", fontWeight: 700, color: "#0C2D48", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Tax year {taxYearLabel} (Apr–Mar)
        </h3>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem" }}>
        {cells.map(c => (
          <div key={c.label} style={{ flex: "1 1 140px", minWidth: 120, background: "#f5f7f9", border: "1px solid #c5cdd4", borderRadius: 10, padding: "0.7rem 0.85rem" }}>
            <div style={{ fontFamily: "Poppins, sans-serif", fontSize: "0.66rem", fontWeight: 700, color: "#4a5a5b", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.2rem" }}>
              {c.label}
            </div>
            <div style={{ fontFamily: "Raleway, sans-serif", fontSize: "1.2rem", fontWeight: 900, color: c.color }}>
              {c.value}
            </div>
          </div>
        ))}
      </div>
      <p style={{ margin: "0.75rem 0 0", fontFamily: "Raleway, sans-serif", fontSize: "0.7rem", color: "#4a5a5b", lineHeight: 1.5 }}>
        Financial projections are estimates based on your current data. Always consult your accountant for tax and compliance advice.
      </p>
    </div>
  );
}

// ─── Export dropdown ──────────────────────────────────────────────────────────

function ExportMenu({ invoices, expenses, start, end, allProjects }) {
  const [open,    setOpen]    = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Grab company profile from context for the PDF header
  const { currentUser } = useContext(CurrentUserContext) || {};
  const profile = currentUser?.preferences || {};

  async function handlePDF() {
    setOpen(false);
    setPdfBusy(true);
    try {
      await exportToPDF(invoices, expenses, start, end, profile, allProjects || []);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Could not generate PDF — please try again.');
    } finally {
      setPdfBusy(false);
    }
  }

  function handleCSV() {
    setOpen(false);
    exportToCSV(invoices, expenses, start, end, allProjects || []);
  }

  const menuItemStyle = {
    display: "flex", alignItems: "center", gap: "0.55rem",
    width: "100%", padding: "0.65rem 1rem", background: "none", border: "none",
    textAlign: "left", cursor: "pointer", fontSize: "0.88rem",
    fontFamily: "Raleway, sans-serif", color: "#333",
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => !pdfBusy && setOpen(o => !o)}
        disabled={pdfBusy}
        style={{ ...btnPrimary, opacity: pdfBusy ? 0.65 : 1, cursor: pdfBusy ? "wait" : "pointer" }}
      >
        {pdfBusy ? "Generating…" : "Export report ▾"}
      </button>

      {open && (
        <div style={{
          position: "absolute", left: 0, top: "calc(100% + 4px)",
          background: "#fff", border: "1px solid #ddd", borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100,
          minWidth: 230, maxWidth: "calc(100vw - 2rem)", overflow: "hidden",
        }}>
          <button
            onClick={handlePDF}
            style={menuItemStyle}
            onMouseEnter={e => e.currentTarget.style.background = "#f8f9fb"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <span>📄</span>
            <span>
              <strong style={{ display: "block", fontSize: "0.88rem", color: "#0C2D48" }}>Export as PDF</strong>
              <span style={{ fontSize: "0.75rem", color: "#888" }}>Professional financial report</span>
            </span>
          </button>
          <div style={{ height: 1, background: "#f0f0f0", margin: "0 0.75rem" }} />
          <button
            onClick={handleCSV}
            style={menuItemStyle}
            onMouseEnter={e => e.currentTarget.style.background = "#f8f9fb"}
            onMouseLeave={e => e.currentTarget.style.background = "none"}
          >
            <span>📊</span>
            <span>
              <strong style={{ display: "block", fontSize: "0.88rem", color: "#0C2D48" }}>Export as CSV</strong>
              <span style={{ fontSize: "0.75rem", color: "#888" }}>For accounting software import</span>
            </span>
          </button>
        </div>
      )}
      {open && <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * FinancialDashboard
 *
 * Props:
 *   onNavigate — optional callback(filterKey) from App.js
 *                Used by "View" / "Add to invoice" buttons to go to Invoicing
 *   onOpenProjectFinancials — optional callback(projectId)
 *                Used by project names in the income breakdown
 */
export default function FinancialDashboard({ onNavigate, onOpenProjectFinancials }) {
  const isMobileLayout = typeof window !== 'undefined'
    && (window.innerWidth <= 1024 || window.matchMedia('(pointer: coarse)').matches);
  const {
    preset, setPreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    start, end,
    metrics,
    trendData,
    byProject,
    byCategory,
    annualSummary,
    overdueInvoices,
    upcomingInvoices,
    unbilledExpenses,
    allProjects,
    invoices,
    expenses,
  } = useFinancials();

  return (
    <div style={{ background: "#f8f9fb", minHeight: "100vh", fontFamily: "Raleway, sans-serif" }}>

      {/* ── Sticky date range bar ── */}
      <DateRangeSelector
        preset={preset} setPreset={setPreset}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
        start={start} end={end}
      />

      {/* ── Scrollable content ── */}
      <div style={{ padding: isMobileLayout ? "0.75rem" : "1.25rem 1.4rem", maxWidth: 1200, margin: "0 auto" }}>

        {/* Page header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem", flexWrap: "wrap", gap: "0.75rem" }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "Poppins, sans-serif", fontSize: "1.4rem", color: "#0C2D48" }}>
              Financial Overview
            </h2>
            {start && end && (
              <div style={{ fontSize: "0.82rem", color: "#aaa", marginTop: "0.15rem" }}>
                {fmtDate(start)} — {fmtDate(end)}
              </div>
            )}
          </div>
          <ExportMenu invoices={invoices} expenses={expenses} start={start} end={end} allProjects={allProjects} />
        </div>

        {/* ── SECTION 1: Key Metrics ── */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.25rem", flexDirection: isMobileLayout ? "column" : "row" }}>
          <FinancialMetricCard
            icon="📄" label="Total invoiced"
            value={fmtMoney(metrics.totalInvoiced)}
            sub={`${metrics.invoiceCount} invoice${metrics.invoiceCount !== 1 ? "s" : ""}`}
          />
          <FinancialMetricCard
            icon="✓" label="Received"
            value={fmtMoney(metrics.totalReceived)}
            sub={`${metrics.paidCount} invoice${metrics.paidCount !== 1 ? "s" : ""} paid`}
            color="#1e6645"
          />
          <FinancialMetricCard
            icon="⏳" label="Pending"
            value={fmtMoney(metrics.totalPending)}
            sub={`${metrics.pendingCount} invoice${metrics.pendingCount !== 1 ? "s" : ""}`}
            color="#7a4a10"
          />
          <FinancialMetricCard
            icon="⚠" label="Overdue"
            value={fmtMoney(metrics.totalOverdue)}
            sub={metrics.overdueCount > 0 ? `${metrics.overdueCount} overdue` : "All clear"}
            color={metrics.overdueCount > 0 ? "#A12710" : "#1e6645"}
          />
          <FinancialMetricCard
            icon="💸" label="Total expenses"
            value={fmtMoney(metrics.totalExpenses)}
            sub={`${metrics.expenseCount} expense${metrics.expenseCount !== 1 ? "s" : ""}`}
            color="#7a4a10"
          />
          <FinancialMetricCard
            icon="£" label="Net profit"
            value={fmtMoney(metrics.netProfit)}
            sub="Revenue minus expenses"
            color={metrics.netProfit >= 0 ? "#1e6645" : "#A12710"}
          />
        </div>

        {/* ── SECTION 2: Revenue Trend ── */}
        <div style={SECTION}>
          <h3 style={SECTION_TITLE}>Revenue trend</h3>
          <RevenueChart data={trendData} />
        </div>

        {/* ── SECTION 3: Income Breakdown ── */}
        {byProject.length > 0 && (
          <div style={SECTION}>
            <h3 style={SECTION_TITLE}>Income by project</h3>
            <IncomeBreakdown byProject={byProject} onOpenProjectFinancials={onOpenProjectFinancials} />
          </div>
        )}

        {/* ── SECTION 4: Expense Breakdown ── */}
        {expenses.length > 0 && (
          <div style={SECTION}>
            <h3 style={SECTION_TITLE}>Expenses by category</h3>
            <ExpenseChart data={byCategory} />
          </div>
        )}

        {/* ── SECTION 5: Outstanding Actions ── */}
        <div style={{ marginBottom: "1.25rem" }}>
          <h3 style={{ ...SECTION_TITLE, marginBottom: "0.75rem" }}>Outstanding actions</h3>
          <OutstandingActions
            overdueInvoices={overdueInvoices}
            upcomingInvoices={upcomingInvoices}
            unbilledExpenses={unbilledExpenses}
            onNavigateInvoicing={onNavigate}
            allProjects={allProjects}
          />
        </div>

        {/* ── SECTION 6: Annual Summary ── */}
        <AnnualSummaryStrip summary={annualSummary} />

      </div>
    </div>
  );
}
